import { Button } from "@bowfast/ui/components/button";
import { Skeleton } from "@bowfast/ui/components/skeleton";
import { cn } from "@bowfast/ui/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { DownloadIcon, SearchIcon, UserPlusIcon, UsersIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { AtSeaSpotlight } from "~/components/crew/AtSeaSpotlight";
import { CrewMemberCard } from "~/components/crew/CrewMemberCard";
import { ListLoadError } from "~/components/shared/ListLoadError";
import { ListSkeleton } from "~/components/shared/ListSkeleton";
import { fadeUp } from "~/lib/animations";
import { createSdk } from "~/lib/gql";
import { useOrg } from "~/lib/org-context";
import { TABS } from "~/pages/crew/crew.config";
import { filterCrew } from "~/pages/crew/crew.filters";
import { buildMetrics } from "~/pages/crew/crew.metrics";
import type { TabKey } from "~/pages/crew/crew.types";

// ── Page ──────────────────────────────────────────────────────────────────────

export function CrewListPage() {
  const auth = useAuth();
  const { orgId } = useOrg();
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data, isPending, isError } = useQuery({
    queryKey: ["org-crew", orgId],
    queryFn: () => createSdk(auth.user?.access_token ?? "").OrgCrewList({ orgId }),
    enabled: !!auth.user?.access_token && !!orgId,
    staleTime: 60 * 1000,
  });

  const members = useMemo(() => data?.organisation?.members ?? [], [data]);

  const metrics = useMemo(() => buildMetrics(members), [members]);

  const filtered = useMemo(() => filterCrew(members, activeTab, searchQuery), [members, activeTab, searchQuery]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <div className="mx-auto w-full max-w-350 space-y-5 p-4 md:p-6">
      {/* ── Page header ── */}
      <motion.div {...fadeUp} className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex size-11 items-center justify-center rounded-xl bg-abyss-50 dark:bg-abyss-800">
            <UsersIcon className="size-6 text-abyss-500" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight md:text-[1.375rem]">Crew</h1>
            <p className="text-sm text-muted-foreground">Your organisation roster</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-2">
            <DownloadIcon className="size-3.5" />
            Export
          </Button>
          <Button size="sm" className="gap-2">
            <UserPlusIcon className="size-3.5" />
            Invite crew
          </Button>
        </div>
      </motion.div>

      {/* ── Metrics strip ── */}
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 }}>
        <div className="flex flex-wrap gap-2">
          {isPending
            ? (["pill-a", "pill-b", "pill-c", "pill-d"] as const).map((key) => (
                <Skeleton key={key} className="h-9 w-28 rounded-lg" />
              ))
            : metrics.map((m) => {
                const Icon = m.Icon;
                return (
                  <div
                    key={m.label}
                    className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2"
                  >
                    <Icon className={cn("size-3.5", m.color)} />
                    <span className="font-heading text-sm font-bold tabular-nums">{m.value}</span>
                    <span className="text-xs text-muted-foreground">{m.label}</span>
                  </div>
                );
              })}
        </div>
      </motion.div>

      {/* ── At-sea spotlight banner ── */}
      {!isPending && members.some((m) => m.user.crewMemberships.some((cm) => cm.expedition.status === "ACTIVE")) && (
        <AtSeaSpotlight members={members} />
      )}

      {/* ── Toolbar: tabs + search ── */}
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.1 }}
        className="flex flex-wrap items-center gap-3"
      >
        {/* Tabs */}
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "relative rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                activeTab === tab.key ? "text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              {activeTab === tab.key && (
                <motion.div
                  layoutId="crew-tab-bg"
                  className="absolute inset-0 rounded-md bg-abyss-500"
                  transition={{ type: "spring", stiffness: 500, damping: 35 }}
                />
              )}
              <span className="relative z-10">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Search */}
        <div className="relative">
          <SearchIcon className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search crew…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 w-48 rounded-lg border border-input bg-card pl-8 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20"
          />
        </div>
      </motion.div>

      {/* ── Loading skeletons ── */}
      {isPending && <ListSkeleton />}

      {/* ── Error state ── */}
      {isError && !isPending && (
        <ListLoadError
          title="Couldn't load your crew"
          onRetry={() => queryClient.invalidateQueries({ queryKey: ["org-crew", orgId] })}
        />
      )}

      {/* ── Crew cards ── */}
      {!isPending && !isError && (
        <motion.div
          className="space-y-3"
          initial="initial"
          animate="animate"
          variants={{ animate: { transition: { staggerChildren: 0.04, delayChildren: 0.14 } } }}
        >
          {filtered.map((member) => (
            <motion.div key={member.id} variants={fadeUp}>
              <CrewMemberCard
                member={member}
                isExpanded={expandedId === member.id}
                onToggle={() => toggleExpand(member.id)}
              />
            </motion.div>
          ))}

          {filtered.length === 0 && (
            <motion.div {...fadeUp} className="flex flex-col items-center gap-3 py-16 text-center">
              <div className="flex size-14 items-center justify-center rounded-2xl bg-muted">
                <SearchIcon className="size-7 text-muted-foreground/40" />
              </div>
              <p className="font-heading text-lg font-bold">
                {members.length === 0 ? "No crew yet — invite your first sailor" : "No crew match these filters"}
              </p>
              <p className="text-sm text-muted-foreground">
                {members.length === 0
                  ? "Use the Invite crew button to bring your team aboard."
                  : "Try a different search or tab."}
              </p>
            </motion.div>
          )}
        </motion.div>
      )}
    </div>
  );
}
